import useOpoPayments from "@nkr/store/reports/hooks/use-opo-payments";
import ArApNav from "../ar-ap-nav";
import { DropdownButton, MenuItem, Well } from "react-bootstrap";
import { ReportContainer, ReportWell } from "../styles";
import { ReportWrapper } from "../accounts-payable/styles";
import { AgGridReact } from "ag-grid-react";
import { CellClassParams, ColDef, FirstDataRenderedEvent, ICellRendererParams, IHeaderParams, SortChangedEvent } from "ag-grid-community";
import { IAccountsPayableOpo } from "@nkr/models/reports";
import SortableHeaderComponent from "@nkr/views/admin/center/list/common/sortable-header";
import RankRenderer from "@nkr/views/admin/center/list/common/rank-renderer";
import BankInfoModal from "../accounts-payable-payment/bank-info-modal";
import ConfirmationFileModal from "../accounts-payable-payment/confirmation-file-modal";
import useExportOpoPaymentList from "@nkr/store/reports/hooks/use-export-opo-payment-list";
import { excelStyles } from "@nkr/store/reports/hooks/use-export-payment-list";
import ActionsButtonOpo from "./actions-button-opo";
import DetailsModalOpo from "./details-modal-opo";
import { useRef } from "react";

export default function AccountsPayableOpoPayment({ location }) {

    const { list, compareBankInfo, updateAchInfo, exportPaymentFile, importConfirmationFile, markPaid } = useOpoPayments();
    const { onGridReady: exportOnGridReady, exportPaymentList } = useExportOpoPaymentList();
    const gridApiRef = useRef<any>(null);
    const selectedIdsRef = useRef<Set<number>>(new Set());

    const fmtValue = (value: number) => {
        if (value == 0) return <>-</>;
        return <>{value.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>;
    };

    const columns: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'R#',
            headerClass: 'list-header',
            cellClass: 'list-cell-center',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.node.rowPinned ? null : <RankRenderer {...params} />,
            width: 50,
            suppressSizeToFit: true,
            valueGetter: (params) => params.node.rowIndex + 1
        },
        {
            colId: 'select',
            headerClass: 'list-header list-header-checkbox',
            cellClass: 'list-cell-center',
            width: 25,
            suppressSizeToFit: true,
            sortable: false,
            resizable: false,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            suppressMenu: true,
        },
        {
            colId: 'opoAlias',
            field: 'opoAlias',
            headerName: 'OPO',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>OPO</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.node.rowPinned ? 'Totals:' : params.value,
            width: 200,
            cellClass: 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'achRoutingNumber',
            field: 'achRoutingNumber',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>System ABA<br/>Routing Number</SortableHeaderComponent>,
            width: 155,
            cellClass: (params: CellClassParams) =>
                params.data.compAchRoutingNumber && params.data.compAchRoutingNumber !== params.data.achRoutingNumber
                    ? 'list-cell-center bg-risk'
                    : 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'compAchRoutingNumber',
            field: 'compAchRoutingNumber',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Chase ABA<br/>Routing Number</SortableHeaderComponent>,
            width: 155,
            cellClass: 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'achAccountNumber',
            field: 'achAccountNumber',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>System<br/>Account Number</SortableHeaderComponent>,
            width: 155,
            cellClass: (params: CellClassParams) =>
                params.data.compAchAccountNumber && params.data.compAchAccountNumber !== params.data.achAccountNumber
                    ? 'list-cell-center bg-risk'
                    : 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'compAchAccountNumber',
            field: 'compAchAccountNumber',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Chase<br/>Account Number</SortableHeaderComponent>,
            width: 155,
            cellClass: (params: CellClassParams) =>
                params.data.compAchAccountNumber && params.data.compAchAccountNumber !== params.data.achAccountNumber
                    ? 'list-cell-center bg-risk'
                    : 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'payNow',
            field: 'payNow',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Pay Now</SortableHeaderComponent>,
            width: 140,
            cellClass: (params: CellClassParams) =>
                params.data.confirmationAmount != null && params.data.confirmationAmount !== params.data.payNow
                    ? ['list-cell-right', 'currency', 'bg-risk']
                    : ['list-cell-right', 'currency'],
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => fmtValue(params.value)
        },
        {
            colId: 'confirmationAmount',
            field: 'confirmationAmount',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Confirmation<br/>Amount</SortableHeaderComponent>,
            width: 140,
            cellClass: (params: CellClassParams) =>
                params.data.confirmationAmount != null && params.data.confirmationAmount !== params.data.payNow
                    ? ['list-cell-right', 'currency', 'bg-risk']
                    : ['list-cell-right', 'currency'],
            sortable: true,
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => params.value != null ? fmtValue(params.value) : null
        },
        {
            colId: 'confirmationNumber',
            field: 'confirmationNumber',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Chase Txn #</SortableHeaderComponent>,
            width: 155,
            cellClass: 'list-cell-center',
            sortable: true,
            resizable: false
        },
        {
            colId: 'detail',
            headerName: '',
            headerClass: 'list-header',
            width: 75,
            sortable: false,
            resizable: false,
            cellClass: 'list-cell-center',
            cellStyle: { overflow: 'visible', zIndex: 9999 },
            suppressMenu: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.node.rowPinned) return null;
                return <DetailsModalOpo ap={params.data} onMarkPaid={markPaid} />;
            }
        },
        {
            colId: 'actions',
            headerName: '',
            headerClass: 'list-header',
            width: 75,
            sortable: false,
            resizable: false,
            cellClass: 'list-cell-center',
            cellStyle: { overflow: 'visible', zIndex: 9999 },
            suppressMenu: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.node.rowPinned) return null;
                return <ActionsButtonOpo ap={params.data} onUpdateAchInfo={updateAchInfo} />;
            }
        },
    ];

    return (
        <div className='container-fluid'>
            <ArApNav href={location.pathname} />
            <div style={ReportContainer}>
                <Well bsSize="sm" style={ReportWell}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>

                    </div>
                    <div>
                        <DropdownButton title="ACH Payment Actions" id="opo-payment-actions" bsStyle="primary">
                            <BankInfoModal onBankInfoUploaded={compareBankInfo} />
                            <MenuItem onClick={exportPaymentList}>Export Payment List</MenuItem>
                            <MenuItem divider />
                            <MenuItem onClick={() => exportPaymentFile(gridApiRef.current?.getSelectedRows() ?? [])}>Export Chase ACH Trxns</MenuItem>
                            <ConfirmationFileModal onConfirmationFileUploaded={importConfirmationFile} />
                        </DropdownButton>
                    </div>
                </Well>
                <ReportWrapper className='ag-theme-bootstrap'>
                    <AgGridReact
                        columnDefs={columns}
                        rowSelection="multiple"
                        onSelectionChanged={(e) => {
                            const rows = e.api.getSelectedRows();
                            if (rows.length > 0)
                                selectedIdsRef.current = new Set(rows.map((r: IAccountsPayableOpo) => r.opoId));
                        }}
                        defaultColDef={{}}
                        rowData={list}
                        pinnedTopRowData={[{
                            payNow: list.reduce((sum, p) => sum + p.payNow, 0),
                            confirmationAmount: list.some(p => p.confirmationAmount != null)
                                ? list.reduce((sum, p) => sum + (p.confirmationAmount ?? 0), 0)
                                : null
                        }]}
                        headerHeight={60}
                        groupHeaderHeight={60}
                        rowHeight={30}
                        suppressRowClickSelection={true}
                        suppressCellSelection={true}
                        noRowsOverlayComponentFramework={() => <div>No records found...</div>}
                        excelStyles={excelStyles}
                        onGridReady={(e) => { gridApiRef.current = e.api; exportOnGridReady(e); }}
                        onFirstDataRendered={(e: FirstDataRenderedEvent) => { e.api.selectAll(); e.api.sizeColumnsToFit(); }}
                        onGridSizeChanged={(e) => e.api.sizeColumnsToFit()}
                        onRowDataChanged={(e) => {
                            e.api.forEachNode(node => {
                                if (selectedIdsRef.current.has(node.data.opoId))
                                    node.setSelected(true);
                            });
                            e.api.sizeColumnsToFit();
                        }}
                        onSortChanged={(e: SortChangedEvent) => { e.api.refreshCells(); }}
                        getRowStyle={(params: any) => {
                            if (params.node.rowPinned) {
                                return { backgroundColor: 'lightblue', fontWeight: 700 } as any;
                            }
                        }}
                    />
                </ReportWrapper>
            </div>
        </div>
    );
}
